import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { type LegacyResponseApplicationError } from '@verantos-dev/types-common';

import { useCohortCreation } from './useCohortCreation';
import { useCohortName } from './useCohortName';
import { useCohortVersionData } from './useCohortVersionData';
import { useProjectData } from './useProjectData';
import { useCohortModalContext } from '../../../context/modalContext';
import { waitForData } from '../../../utils/waitForData';

export const useCopyCohortModal = () => {
  const { projectId } = useParams();
  const { closeDialog, itemId, itemName, latestCohortVersionId } = useCohortModalContext();

  const { newCohortName, handleCohortNameChange } = useCohortName(itemName || '');
  const { projectData, isProjectLoading } = useProjectData(projectId);
  const { sourceCohortVersion, isCohortVersionLoading } = useCohortVersionData(
    projectId,
    itemId,
    latestCohortVersionId
  );

  const { handleSubmit, isCreatingCohort, isCreatingVersion } = useCohortCreation(
    projectId,
    projectData,
    sourceCohortVersion,
    newCohortName,
    itemName
  );

  const [submittingError, setSubmittingError] = useState<
    FetchBaseQueryError | SerializedError | LegacyResponseApplicationError[] | undefined
  >(undefined);

  const handleSubmitWithDataCheck = useCallback(async () => {
    if (!itemId || !newCohortName.trim()) return;

    try {
      // Wait for project data
      await waitForData(() => Boolean(projectData?.data?.datasetReleaseId), isProjectLoading);

      // Wait for cohort version data
      await waitForData(() => Boolean(sourceCohortVersion?.data), isCohortVersionLoading);

      if (!projectData?.data?.datasetReleaseId || !sourceCohortVersion?.data) {
        throw new Error(
          `Unable to load the required data to copy cohort ${itemName}. Please try again later.`
        );
      }

      return handleSubmit();
    } catch (error) {
      console.error('Error copying cohort:', error);
      if (error instanceof Error) {
        if (error.message === 'Timeout waiting for data') {
          setSubmittingError({
            message: 'Unable to load required data at this time. Please try again later.',
          });
        }
        if (error.message === 'Data loading completed but required data is missing') {
          setSubmittingError({
            message: 'Required data is not available. Please try again later.',
          });
        }
      }
    }
  }, [
    itemId,
    newCohortName,
    projectData,
    isProjectLoading,
    sourceCohortVersion,
    isCohortVersionLoading,
    itemName,
    handleSubmit,
  ]);

  return {
    closeDialog,
    newCohortName,
    handleCohortNameChange,
    handleSubmit: handleSubmitWithDataCheck,
    isSubmitting: isCreatingCohort || isCreatingVersion,
    isValidForSubmit: Boolean(
      newCohortName.trim().length > 0 && itemId && sourceCohortVersion?.data
    ),
    submittingError,
  };
};
